<template>
  <div class="student-table-wrapper">
    <div class="student-table-container">
      <div class="button-wrapper">
        <button 
          @click="toggleHideInactive"
          :class="{ active: hideInactive }"
        >
          {{ hideInactive ? "Show All" : "Hide Inactive" }}
        </button>
      </div>
      <table class="student-table">
        <thead>
          <tr>
            <th>Patient/Student Name</th>
            <th>Screening Taken</th>
            <th>Screening Upcoming</th>
            <th>Assessments Taken</th>
            <th>Assessments Upcoming</th>
            <th>Intervention Taken</th>
            <th>Intervention Upcoming</th>
            <th>Active</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="student in paginatedStudents" :key="student.child_id">
            <td @click="goToChild(student.child_id)" class="clickable-name">
              {{ student.first_name }} {{ student.last_name }}
            </td>
            <td>{{ student.screenings.progress }}</td>
            <td>{{ student.screenings.remaining.join(", ") }}</td>
            <td>{{ student.assessments.progress }}</td>
            <td>{{ student.assessments.remaining.join(", ") }}</td>
            <td>{{ student.interventions.progress }}</td>
            <td>{{ student.interventions.remaining.join(", ") }}</td>
            <td>
              <span :class="student.activity_status ? 'active' : 'inactive'">
                {{ student.activity_status ? "Active" : "Inactive" }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination-wrapper">
      <div class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1">Previous</button>

        <button
          v-for="page in paginationRange"
          :key="page"
          @click="page !== '...' && goToPage(page)"
          :disabled="page === '...'"
          :class="{ active: currentPage === page }"
        >
          {{ page }}
        </button>

        <button @click="nextPage" :disabled="currentPage === totalPages">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: "StudentTable",
  data() {
    return {
      students: [], // Filled from API
      currentPage: 1,
      studentsPerPage: 8,
      hideInactive: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.students.length / this.studentsPerPage);
    },
    paginatedStudents() {
      const start = (this.currentPage - 1) * this.studentsPerPage;
      const end = start + this.studentsPerPage;
      return this.students.slice(start, end);
    },
    paginationRange() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];

      let left = current - delta;
      let right = current + delta;

      if (left < 1) {
        left = 1;
        right = Math.min(total, left + delta * 2);
      }
      if (right > total) {
        right = total;
        left = Math.max(1, right - delta * 2);
      }

      for (let i = left; i <= right; i++) {
        range.push(i);
      }

      if (range[0] > 1) {
        range.unshift(1);
        if (range[1] > 2) range.splice(1, 0, "...");
      }
      if (range[range.length - 1] < total) {
        range.push(total);
        if (range[range.length - 2] < total - 1)
          range.splice(range.length - 1, 0, "...");
      }

      return range;
    },
  },
  methods: {
    goToPage(page) {
      this.currentPage = page;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    toggleHideInactive() {
      this.hideInactive = !this.hideInactive;
      this.fetchSearchFilter();
    },
    goToChild(childID) {
      this.$router.push(`/specialistGoals/Screening/${childID}`);
    },
    async fetchSearchFilter() {
      console.log("Fetching students...");

      try {
        const baseUrl = process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || "http://localhost:8081";

        // get specialist_id from cookie
        const authResp = await fetch(`${baseUrl}/api/auth/get-access-cookie-data`, {
          credentials: "include",
        });
        if (!authResp.ok) throw new Error("Failed to fetch auth cookie data");
        const authData = await authResp.json();
        const ID = authData.id;

        const params = new URLSearchParams();
                if (this.sortBy) params.append("sort", this.sortBy);
                if (this.searchQuery) params.append("search", this.searchQuery);
                if (this.hideInactive) params.append("hideInactive", "true");

                const response = await fetch(
                  `${baseUrl}/api/specialist/user/${ID}/searchFilter?${params.toString()}`,
                  {
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                  }
                );    

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();

        // map data into students array
        this.students = data.map((s) => ({
          child_id: s.child_id,
          first_name: s.first_name,
          last_name: s.last_name,
          activity_status: s.activity_status,
          screenings: s.screenings,
          assessments: s.assessments,
          interventions: s.interventions,
        }));

        console.log("Mapped students:", this.students);
      } catch (err) {
        console.error("Error fetching students:", err);
      }
    }
  },
  mounted() {
    this.fetchSearchFilter();
  },
  props: {
  searchQuery: String,
  sortBy: String
 },
  watch: {
    sortBy: { handler: "fetchSearchFilter", immediate: false },
    searchQuery: { handler: "fetchSearchFilter", immediate: false },
  },
};
</script>

<style scoped lang="scss">
.student-table-container {
  padding: 1rem;
  margin-top: 0;
  padding-top: 0;
}

.student-table-container .button-wrapper {
  display: flex;
  justify-content: flex-end;
}

.student-table-container button {
  display: flex;
  justify-content: flex-end;
  //margin-bottom: 1rem;
  padding: 0.25rem 0.5rem;
  color: #98a2b3;
  border: none;
  cursor: pointer;
}

.student-table-container button:hover {
  color: #9D5f0E;
  font-weight: 600;
}

.student-table-container button.active {
  color: #9D5f0E;
  font-weight: 600;
}
.student-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0;

  th,
  td {
    padding: 0.75rem;
    border-bottom: 1px solid #ccc;  
    text-align: center;
  }

  thead {
    position: relative; 
    font-size: 14px;
    color: #98a2b3;

    &::after {
        content: '';
        position: absolute;
        bottom: 0;      
        left: 50%;      
        transform: translateX(-50%);
        width: 1200px; 
        border-bottom: 1px solid #ccc;
        pointer-events: none;  
    }

    th {
        border-bottom: none;  
        font-weight: 400;
    }
  }

  tbody {
    font-weight: 450;
  }

  .active {
    color: green;
    border: 2px solid green; 
    border-radius: 0.5rem;
    padding: 0.4rem 0.8rem;
    display: inline-block;
    background-color: #a6e7d8;
  }

  .inactive {
    color: red;
    border: 2px solid red; 
    border-radius: 0.5rem;
    padding: 0.4rem 0.8rem; 
    display: inline-block;
    background-color: #ffc5c5;
  }
}

.pagination-wrapper{
  display: flex;
  justify-content: flex-end;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}

.pagination button {
  padding: 5px 10px;
  border: 1px solid #ccc;
  background: #fff;
  cursor: pointer;
}

.pagination button.active {
  background: #f97316; /* orange */
  color: white;
  border-color: #f97316;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: default;
}

</style>
